import { Types } from '@/modules/API';
import { i18n } from '@/main';
import Utils from './Utils';
import vuex from '@/store';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment-timezone';
import API from './API';


export default class PropUtils {
  private static get properties() {
    return (vuex.state as any).Props.list;
  }

  static getPropIndex(objectType: Types, objectID, name) {
    let index = -1;
    this.properties.forEach(
      (p, i) =>
        p.objectType === objectType &&
        p.objectID === objectID &&
        p.name === name &&
        p.id !== 'virtual' &&
        (index = i)
    );
    return index;
  }

  static getProp(objectType: Types, objectID, name, value = {} as any): any {
    if (name.toString().indexOf(':') !== -1) name = name.split(':')[0];
    const prop = {
      pseudo: true,
      objectType,
      objectID,
      value,
      name
    };

    return (
      this.properties[this.getPropIndex(objectType, objectID, name)] || prop
    );
  }

  static getPropValue(
    objectType: Types,
    objectID,
    name,
    value = {} as any
  ): any {
    value = this.getProp(objectType, objectID, name, value).value || value;
    if (name.toString().indexOf(':') !== -1) {
      value = name
        .split(':')[1]
        .replace(' ', '')
        .split(',')
        .map((key) => value[key])
        .filter((v) => v)
        .join(', ');
    }

    return value;
  }

  static parseMetaCommissionProperty(device_commission_prop) {
      const commission_prop = {
        date: 'none',
        commissioner: 'none',
        commissioned: false
      };

      if (!device_commission_prop || (typeof device_commission_prop === 'object' && Object.keys(device_commission_prop).length === 0)) {
        return commission_prop;
      }

      return device_commission_prop;
  }

  static parseMetaLocationProperty(device_location_prop, default_location) {
    let has_valid_location = false, has_default_location = false;
    const location_prop = {lat: null, lng: null};
    try {
        location_prop.lat = parseFloat(device_location_prop.lat);
        location_prop.lng = parseFloat(device_location_prop.lng);
        if ((location_prop.lat > 90) || (location_prop.lat < -90) || (location_prop.lng > 180) || (location_prop.lng < -180)) {
          throw new Error('Invalid coordinates');
        }
        if ((location_prop.lat === 0) && (location_prop.lng === 0) ) {
          throw new Error('Invalid coordinates');
        }
        if ( (isNaN(location_prop.lat)) || (isNaN(location_prop.lng)) ) {
          throw new Error('Invalid coordinates');
        }
        has_valid_location = true;
    }
    catch (e) {
      if (!default_location) {
        return [location_prop, has_valid_location, has_default_location];
      }

      has_default_location = true;
      has_valid_location = true;
      location_prop.lat = default_location.lat + (Math.random() * 0.002) - 0.001;
      location_prop.lng = default_location.lng + (Math.random() * 0.002) - 0.001;
    }
    return [location_prop, has_valid_location, has_default_location];
  }



  static getProperty(properties, property_name) {
    return properties.find((result) => result.name === property_name);
  }

  static getProjectPropertyData(properties, property_name) {
    const property = PropUtils.getProperty(properties, property_name);
    if (!property) console.log(`project property ${property_name} does not exist`);
    return property && property['value'] ? property['value'] : null;
  }

  static getDeviceStream(stream_name, streams_list) {
    return streams_list.find((stream) => stream.name === stream_name);
  }

  static getMetaDeviceField(meta_device, field_name) {
    if (!meta_device) return `${i18n.t(field_name)}: ${i18n.t('N/A')}`;
    return `${i18n.t(PropUtils.getFieldLowerToUpper(field_name))}: ${meta_device[field_name] || i18n.t('N/A')}`;
  }

  static getMetaDeviceFieldValue(meta_device, field_name) {
    return !meta_device ? '' : meta_device[field_name];
  }

  static getFieldLowerToUpper(field) {
    if (!field.includes('_') && !field.includes(' ')) return field.slice(0, 1).toUpperCase().concat(field.slice(1));
    let formatted_field = field.replaceAll('_', ' ');
    formatted_field = formatted_field
      .split(' ')
      .map((word) =>
        word
          .slice(0, 1)
          .toUpperCase()
          .concat(word.slice(1))
      )
      .join(' ');
    return formatted_field;
  }

  static getFieldUpperToLower(field){
    const formatted_field = field.toLowerCase();
    if (!field.includes(' ')) return formatted_field;
    return formatted_field.replaceAll(' ', '_');
  }

  static getFieldValue(meta_device, field){
    if (!meta_device || !field) return '';
    const lower = PropUtils.getFieldUpperToLower(field);

    return meta_device.hasOwnProperty(field) 
      ? meta_device[field]
      : meta_device.hasOwnProperty(lower)
        ? meta_device[lower]
        : '';
  }

  static getUppercaseFields(fields){
    return Object.entries(fields).filter(([field_name, value]) => field_name.includes(' ') || /[A-Z]/.test(field_name));
  }

  static getUppercaseFieldsList(fields){
    return Object.entries(fields).map(([field_name, options]) => ({
      name: i18n.t(PropUtils.getFieldLowerToUpper(field_name)),
      value: field_name,
      options
    })).sort((field_data1, field_data2) => field_data1.name.localeCompare(field_data2.name, undefined, {numeric: true, sensitivity: 'base'}));
  }

  static hasAstronomicalClock(device_id){
    let is_astronomical = false;
    const timezone = (vuex.state as any).Global.timezone;
    const today = timezone ? moment().tz(timezone).format('DD.MM.YYYY') : moment().format('DD.MM.YYYY');
    const device = vuex.getters['Reports/commissionedFixtures_map'].get(device_id);
    const device_schedule = device['device.schedule'];

    if (
      Object.entries(device_schedule).length &&
      device_schedule.hasOwnProperty('scheduler_type') &&
      device_schedule.scheduler_type === 'cabinet_scheduler'
    ){
      const meta_device = device['meta.device'];
      let related_cabinet = meta_device.cabinet_id && 
        vuex.getters['Reports/commissionedCabinets'].filter((cabinet) => 
          cabinet['meta.device'].cabinet_id && cabinet['meta.device'].cabinet_id === meta_device.cabinet_id
        );
      related_cabinet = related_cabinet.length && related_cabinet[0] || null;
      if (related_cabinet) {
        const schedule = related_cabinet['meta.devices_schedule'];
        is_astronomical = Object.entries(schedule).length && schedule.is_astronomical || false;
      }
    }else {
      const schedule_curves = device['meta.schedule_curve'];
      if (schedule_curves?.curves && schedule_curves.curves[today]) {
        const today_curve = schedule_curves.curves[today];
        is_astronomical = (today_curve && today_curve.astronomical) || false;
      }
    }

    return is_astronomical;
  }

  static async fetchSingleProperty(type, property_name, format, prefix){
    try {
      const property = await API.get(type, `${prefix}/properties/`, { name: property_name });
      return property && property.results && property.results[0] && property.results[0].value ? property.results[0].value : format === 'json' ? {} : '';
    }catch (e) {
      return format === 'json' ? {} : '';
    }
  }

  static async fetchPropertyHistory(prefix, property_name, days_back = '10days', limit = 10){
    try {
      const property = await API.get('', `${prefix}/properties/${property_name}/history`, { since: days_back, limit });
      return property || [];
    }catch (e) {
      return [];
    }
  }

  static getTeltonicaHardwareid(device){
    const meta_hardwareid = device['meta.hardware_ids'];
    const device_hardwareid = device['device.hardwareid'];
    
    return meta_hardwareid && meta_hardwareid.eth0 
      ? meta_hardwareid.eth0.replaceAll(':', '').toUpperCase()
      : device_hardwareid && device_hardwareid.id 
        ? device_hardwareid.id 
        : '';
  }
}
